import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Send, MessageCircle, ShieldAlert, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [conversations, setConversations] = useState([]);
  const [leader, setLeader] = useState(null);
  const [active, setActive] = useState(null); // {user_id, name, picture}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcast, setBroadcast] = useState("");
  const [sending, setSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const endRef = useRef(null);

  const loadConvos = () => api.get("/messages/conversations").then((r) => setConversations(r.data)).catch(() => {});
  const loadBroadcasts = () => api.get("/messages/broadcasts").then((r) => setBroadcasts(r.data)).catch(() => {});

  const sendBroadcast = async () => {
    if (!broadcast.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages/broadcast", { body: broadcast });
      toast.success(`Message sent to ${data.sent} member${data.sent === 1 ? "" : "s"}`);
      setBroadcast("");
      setShowBroadcast(false);
      loadConvos();
      loadBroadcasts();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  useEffect(() => {
    if (isAdmin) {
      loadConvos();
      loadBroadcasts();
    } else {
      // Members may only message a community leader — auto-open that conversation.
      api.get("/leader-contact")
        .then((r) => { setLeader(r.data); setActive(r.data); })
        .catch((e) => toast.error(apiError(e)));
    }
  }, [isAdmin]);

  // Admins can open a conversation with any member via ?to=user_id (from Members page).
  useEffect(() => {
    if (!isAdmin) return;
    const to = params.get("to");
    if (to) {
      api.get(`/users/${to}`).then((r) => setActive({ user_id: r.data.user_id, name: r.data.name, picture: r.data.picture })).catch(() => {});
    }
  }, [params, isAdmin]);

  useEffect(() => {
    if (!active) return;
    const load = () => api.get(`/messages/${active.user_id}`).then((r) => setMessages(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", { to_user_id: active.user_id, body: text });
      setText("");
      const { data } = await api.get(`/messages/${active.user_id}`);
      setMessages(data);
      if (isAdmin) loadConvos();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const sidebarItems = isAdmin
    ? conversations
    : (leader ? [{ ...leader, last_message: "Your community leader" }] : []);

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <p className="text-xs tracking-[0.2em] uppercase text-[#C46B4E] font-semibold">Messages</p>
        <h1 className="font-heading text-5xl text-[#2A2621] mt-2">Private conversations</h1>
        <p className="text-[#615A50] mt-2">
          {isAdmin
            ? "Private messages between you and members of the collective."
            : "Reach out privately to a community leader for prayer, questions, or support."}
        </p>
      </div>

      {/* Admin-visibility disclaimer */}
      <div className="flex items-start gap-3 rounded-2xl border border-[#C46B4E]/40 bg-[#C46B4E]/5 p-4" data-testid="messages-disclaimer">
        <ShieldAlert className="w-5 h-5 text-[#C46B4E] shrink-0 mt-0.5" />
        <p className="text-sm text-[#615A50]">
          <span className="font-semibold text-[#2A2621]">Please note:</span> for the safety and care of our community,
          administrators (community leaders) can view all private messages.
        </p>
      </div>

      {/* Admin broadcast composer */}
      {isAdmin && (
        <div className="rounded-2xl border border-[#3D5844]/30 bg-[#F3EFE4] p-4" data-testid="broadcast-panel">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#3D5844]" />
              <p className="font-medium text-[#2A2621]">Message all members</p>
            </div>
            <button onClick={() => setShowBroadcast((s) => !s)} data-testid="broadcast-toggle"
              className="text-sm font-medium text-[#3D5844] hover:text-[#2F4535]">{showBroadcast ? "Cancel" : "Compose"}</button>
          </div>
          {showBroadcast && (
            <div className="mt-3 space-y-3">
              <p className="text-sm text-[#615A50]">This sends a private message to every approved member. Each person sees it in their own conversation with you — no one sees who else received it.</p>
              <textarea value={broadcast} onChange={(e) => setBroadcast(e.target.value)} rows={3} placeholder="Write a message to all members…" data-testid="broadcast-input"
                className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" />
              <button onClick={sendBroadcast} disabled={sending || !broadcast.trim()} data-testid="broadcast-send"
                className="rounded-full bg-[#3D5844] text-white px-5 py-2.5 font-medium hover:bg-[#2F4535] disabled:opacity-60">{sending ? "Sending…" : "Send to all members"}</button>
            </div>
          )}
          {broadcasts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#D5CEBF]" data-testid="broadcast-history">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#3D5844] mb-3">Broadcast history</p>
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {broadcasts.map((b) => (
                  <div key={b.id} className="rounded-xl border border-[#D5CEBF] bg-[#FAF7EE] p-3" data-testid={`broadcast-item-${b.id}`}>
                    <p className="text-[15px] text-[#2A2621] whitespace-pre-wrap">{b.body}</p>
                    <p className="text-xs text-[#615A50] mt-1.5">
                      Sent to {b.sent} member{b.sent === 1 ? "" : "s"} · {new Date(b.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 h-[600px]">
        <div className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-4 overflow-y-auto">
          <h2 className="font-heading text-xl text-[#2A2621] px-2 mb-3">{isAdmin ? "Conversations" : "Community Leader"}</h2>
          {sidebarItems.length === 0 && (
            <p className="text-sm text-[#615A50] px-2">{isAdmin ? "No messages yet." : "No community leader is available right now."}</p>
          )}
          {sidebarItems.map((c) => (
            <button key={c.user_id} onClick={() => setActive(c)} data-testid={`convo-${c.user_id}`}
              className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-colors ${active?.user_id === c.user_id ? "bg-[#E7DFCE]" : "hover:bg-[#E7DFCE]/60"}`}>
              {fileUrl(c.picture) ? <img src={fileUrl(c.picture)} alt="" className="w-10 h-10 rounded-full object-cover" /> :
                <div className="w-10 h-10 rounded-full bg-[#3D5844] text-white flex items-center justify-center font-semibold">{c.name?.charAt(0)}</div>}
              <div className="min-w-0">
                <p className="font-medium text-[#2A2621] truncate">{c.name}</p>
                <p className="text-sm text-[#615A50] truncate">{c.last_message}</p>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-[#D5CEBF] bg-[#FAF7EE] flex flex-col">
          {active ? (
            <>
              <div className="flex items-center gap-3 p-4 border-b border-[#D5CEBF]">
                {fileUrl(active.picture) ? <img src={fileUrl(active.picture)} alt="" className="w-9 h-9 rounded-full object-cover" /> :
                  <div className="w-9 h-9 rounded-full bg-[#3D5844] text-white flex items-center justify-center text-sm font-semibold">{active.name?.charAt(0)}</div>}
                <p className="font-medium text-[#2A2621]">{active.name}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => {
                  const mine = m.to_user_id === active.user_id;
                  return (
                    <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-[15px] ${mine ? "bg-[#3D5844] text-white rounded-br-sm" : "bg-[#E7DFCE] text-[#2A2621] rounded-bl-sm"}`}>
                        {m.body}
                      </div>
                      {mine && <span className="text-[10px] text-[#615A50] mt-1 px-1" data-testid={`message-status-${m.id}`}>{m.read_at ? "Read" : "Sent"}</span>}
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-4 border-t border-[#D5CEBF] flex items-center gap-3">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…" data-testid="message-input"
                  className="flex-1 rounded-full border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
                <button type="submit" data-testid="message-send" className="w-11 h-11 rounded-full bg-[#3D5844] text-white flex items-center justify-center hover:bg-[#2F4535]">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-[#615A50]">
              <MessageCircle className="w-10 h-10 text-[#3D5844]/40" />
              <p className="mt-3">{isAdmin ? "Select a conversation, or message a member from the Members page." : "Loading your conversation…"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
